import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiUserPlus, FiEdit, FiTrash2, FiShield, FiLogOut, FiHome, FiSave, FiX, FiUserCheck } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { auth, analytics, logEvent, db } from '../firebase';
import { signOut, onAuthStateChanged, User } from 'firebase/auth';
import { collection, doc, setDoc, getDocs, updateDoc, deleteDoc, query, where } from 'firebase/firestore';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Types
interface Lid {
  id: string;
  email: string;
  naam: string;
  rollen: string[];
  actief: boolean;
  aangemaaktOp: Date;
  laatstGewijzigd: Date;
}

interface Rol {
  id: string;
  naam: string;
  beschrijving: string;
  kleur: string;
}

const LedenBeheer: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [leden, setLeden] = useState<Lid[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLid, setEditingLid] = useState<Lid | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    naam: '',
    rollen: ['Standaardlid']
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLoginAsModal, setShowLoginAsModal] = useState(false);
  const [selectedLid, setSelectedLid] = useState<Lid | null>(null);
  const navigate = useNavigate();

  // Beschikbare rollen
  const beschikbareRollen: Rol[] = [
    { id: 'bestuur', naam: 'Bestuur', beschrijving: 'Lid van het bestuur', kleur: 'from-red-500 to-red-600' },
    { id: 'commissielid', naam: 'Commissielid', beschrijving: 'Lid van een commissie', kleur: 'from-blue-500 to-blue-600' },
    { id: 'admin', naam: 'Admin', beschrijving: 'Beheerder van het systeem', kleur: 'from-purple-500 to-purple-600' },
    { id: 'webbeheerder', naam: 'Webbeheerder', beschrijving: 'Beheerder van de website', kleur: 'from-green-500 to-green-600' },
    { id: 'prac', naam: 'PRAC', beschrijving: 'Public Relations en Communicatie', kleur: 'from-yellow-500 to-yellow-600' },
    { id: 'standaardlid', naam: 'Standaardlid', beschrijving: 'Regulier lid', kleur: 'from-gray-500 to-gray-600' }
  ];

  // Auth state observer
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      
      if (!user) {
        navigate('/leden-login');
      } else if (user.email !== 'p@de-boer.net') {
        // Alleen admin heeft toegang
        setError('U heeft geen toegang tot deze pagina.');
        setTimeout(() => navigate('/leden-dashboard'), 3000);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Google Analytics page view
  useEffect(() => {
    if (analytics && user) {
      logEvent(analytics, 'page_view', {
        page_title: 'Leden Beheer',
        page_location: window.location.href
      });
    }
  }, [user]);

  // Leden laden
  useEffect(() => {
    if (user && user.email === 'p@de-boer.net') {
      loadLeden();
    }
  }, [user]);

  const loadLeden = async () => {
    try {
      const ledenRef = collection(db, 'leden');
      const snapshot = await getDocs(ledenRef);
      const ledenData: Lid[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        ledenData.push({
          id: doc.id,
          email: data.email,
          naam: data.naam,
          rollen: data.rollen || ['Standaardlid'],
          actief: data.actief !== false,
          aangemaaktOp: data.aangemaaktOp?.toDate() || new Date(),
          laatstGewijzigd: data.laatstGewijzigd?.toDate() || new Date()
        });
      });
      
      // Sorteer leden op alfabetische volgorde (naam)
      ledenData.sort((a, b) => a.naam.localeCompare(b.naam, 'nl-NL'));
      
      setLeden(ledenData);
    } catch (err: any) {
      console.error('Error loading leden:', err);
      setError('Fout bij het laden van leden.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      
      if (analytics) {
        logEvent(analytics, 'logout');
      }
      
      navigate('/');
    } catch (err: any) {
      console.error('Logout error:', err);
    }
  };

  const handleAddLid = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email || !formData.naam) {
      setError('Vul alle verplichte velden in.');
      return;
    }

    try {
      const lidData = {
        email: formData.email.toLowerCase(),
        naam: formData.naam,
        rollen: formData.rollen,
        actief: true,
        aangemaaktOp: new Date(),
        laatstGewijzigd: new Date()
      };

      // Check of lid al bestaat
      const existingLid = leden.find(lid => lid.email === lidData.email);
      if (existingLid) {
        setError('Er bestaat al een lid met dit e-mailadres.');
        return;
      }

      const lidRef = doc(collection(db, 'leden'));
      await setDoc(lidRef, lidData);

      setSuccess('Lid succesvol toegevoegd!');
      setFormData({ email: '', naam: '', rollen: ['Standaardlid'] });
      setShowAddForm(false);
      loadLeden();

      if (analytics) {
        logEvent(analytics, 'lid_toegevoegd', {
          email: lidData.email
        });
      }
    } catch (err: any) {
      console.error('Error adding lid:', err);
      setError('Fout bij het toevoegen van lid.');
    }
  };

  const handleEditLid = async (lid: Lid) => {
    setEditingLid(lid);
    setFormData({
      email: lid.email,
      naam: lid.naam,
      rollen: lid.rollen
    });
  };

  const handleUpdateLid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLid) return;

    try {
      const lidRef = doc(db, 'leden', editingLid.id);
      await updateDoc(lidRef, {
        naam: formData.naam,
        rollen: formData.rollen,
        laatstGewijzigd: new Date()
      });

      setSuccess('Lid succesvol bijgewerkt!');
      setEditingLid(null);
      setFormData({ email: '', naam: '', rollen: ['Standaardlid'] });
      loadLeden();

      if (analytics) {
        logEvent(analytics, 'lid_bijgewerkt', {
          email: editingLid.email
        });
      }
    } catch (err: any) {
      console.error('Error updating lid:', err);
      setError('Fout bij het bijwerken van lid.');
    }
  };

  const handleDeleteLid = async (lidId: string, email: string) => {
    if (!window.confirm(`Weet u zeker dat u het lid ${email} wilt verwijderen?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'leden', lidId));
      setSuccess('Lid succesvol verwijderd!');
      loadLeden();

      if (analytics) {
        logEvent(analytics, 'lid_verwijderd', {
          email: email
        });
      }
    } catch (err: any) {
      console.error('Error deleting lid:', err);
      setError('Fout bij het verwijderen van lid.');
    }
  };

  const toggleRol = (rol: string) => {
    const nieuweRollen = formData.rollen.includes(rol)
      ? formData.rollen.filter(r => r !== rol)
      : [...formData.rollen, rol];
    
    // Zorg ervoor dat Standaardlid altijd aanwezig is
    if (!nieuweRollen.includes('Standaardlid')) {
      nieuweRollen.push('Standaardlid');
    }
    
    setFormData({ ...formData, rollen: nieuweRollen });
  };

  // Functie om als een andere gebruiker in te loggen
  const loginAsUser = async (lid: Lid) => {
    try {
      // Sla de huidige admin gebruiker op in localStorage
      localStorage.setItem('adminUser', JSON.stringify({
        email: user?.email,
        timestamp: Date.now()
      }));

      // Simuleer login als de geselecteerde gebruiker
      localStorage.setItem('loginAsUser', JSON.stringify({
        email: lid.email,
        naam: lid.naam,
        rollen: lid.rollen,
        timestamp: Date.now()
      }));

      setSuccess(`Nu ingelogd als ${lid.naam} (${lid.email})`);
      
      // Navigeer naar dashboard
      setTimeout(() => {
        navigate('/leden-dashboard');
      }, 1500);

      // Google Analytics - login as event
      if (analytics) {
        logEvent(analytics, 'admin_login_as', {
          admin_email: user?.email,
          target_user: lid.email,
          target_roles: lid.rollen.join(', ')
        });
      }
    } catch (error) {
      console.error('Error logging in as user:', error);
      setError('Fout bij inloggen als gebruiker.');
    }
  };

  // Functie om terug te gaan naar admin account
  const logoutFromUser = () => {
    localStorage.removeItem('loginAsUser');
    localStorage.removeItem('adminUser');
    setSuccess('Terug naar admin account');
    
    // Refresh de pagina om de auth state te resetten
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  if (!user || user.email !== 'p@de-boer.net') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white">
          <FiShield className="text-6xl mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold mb-2">Toegang Geweigerd</h1>
          <p className="text-gray-300">U heeft geen toegang tot deze pagina.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <FiUsers className="text-xl text-indigo-900" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Leden Beheer</h1>
                <p className="text-gray-300 text-sm">Beheer leden en rollen</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right text-white">
                <p className="text-sm text-gray-300">Admin</p>
                <p className="font-medium">{user.email}</p>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => navigate('/leden-dashboard')}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  title="Terug naar dashboard"
                >
                  <FiHome className="text-lg" />
                </button>
                
                <button
                  onClick={handleLogout}
                  className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <FiLogOut className="text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Error/Success Messages */}
          {error && (
            <motion.div 
              variants={fadeIn}
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center"
            >
              <FiX className="mr-2 flex-shrink-0" />
              <p>{error}</p>
            </motion.div>
          )}
          
          {success && (
            <motion.div 
              variants={fadeIn}
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center"
            >
              <FiSave className="mr-2 flex-shrink-0" />
              <p>{success}</p>
            </motion.div>
          )}

          {/* Add New Lid Button */}
          <motion.div variants={fadeIn} className="mb-8">
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 flex items-center"
            >
              <FiUserPlus className="mr-2" />
              Nieuw Lid Toevoegen
            </button>
          </motion.div>

          {/* Add/Edit Form */}
          {(showAddForm || editingLid) && (
            <motion.div 
              variants={fadeIn}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 mb-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">
                {editingLid ? 'Lid Bewerken' : 'Nieuw Lid Toevoegen'}
              </h2>
              
              <form onSubmit={editingLid ? handleUpdateLid : handleAddLid} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white mb-2 font-medium">E-mailadres *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled={!!editingLid}
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="lid@email.nl"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white mb-2 font-medium">Naam *</label>
                    <input
                      type="text"
                      value={formData.naam}
                      onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="Voor- en achternaam"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white mb-4 font-medium">Rollen *</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {beschikbareRollen.map((rol) => (
                      <label key={rol.id} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.rollen.includes(rol.naam)}
                          onChange={() => toggleRol(rol.naam)}
                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                        <span className="text-white">{rol.naam}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm mt-2">
                    * Standaardlid wordt automatisch toegevoegd
                  </p>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <FiSave className="inline mr-2" />
                    {editingLid ? 'Bijwerken' : 'Toevoegen'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingLid(null);
                      setFormData({ email: '', naam: '', rollen: ['Standaardlid'] });
                    }}
                    className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
                  >
                    Annuleren
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Leden List */}
          <motion.div variants={fadeIn}>
            <h2 className="text-2xl font-bold text-white mb-6">Leden Overzicht</h2>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/20">
                    <tr>
                      <th className="px-6 py-3 text-left text-white font-medium">Naam</th>
                      <th className="px-6 py-3 text-left text-white font-medium">E-mail</th>
                      <th className="px-6 py-3 text-left text-white font-medium">Rollen</th>
                      <th className="px-6 py-3 text-left text-white font-medium">Status</th>
                      <th className="px-6 py-3 text-left text-white font-medium">Acties</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {leden.map((lid) => (
                      <tr key={lid.id} className="hover:bg-white/5">
                        <td className="px-6 py-4 text-white font-medium">{lid.naam}</td>
                        <td className="px-6 py-4 text-gray-300">{lid.email}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {lid.rollen.map((rol) => {
                              const rolInfo = beschikbareRollen.find(r => r.naam === rol);
                              return (
                                <span
                                  key={rol}
                                  className={`px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${rolInfo?.kleur || 'from-gray-500 to-gray-600'} text-white`}
                                >
                                  {rol}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            lid.actief 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {lid.actief ? 'Actief' : 'Inactief'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => loginAsUser(lid)}
                              className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                              title="Login als deze gebruiker"
                            >
                              <FiUserCheck className="text-sm" />
                            </button>
                            <button
                              onClick={() => handleEditLid(lid)}
                              className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                              title="Bewerken"
                            >
                              <FiEdit className="text-sm" />
                            </button>
                            <button
                              onClick={() => handleDeleteLid(lid.id, lid.email)}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                              title="Verwijderen"
                            >
                              <FiTrash2 className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {leden.length === 0 && (
                <div className="text-center py-8 text-gray-300">
                  <FiUsers className="text-4xl mx-auto mb-4 opacity-50" />
                  <p>Nog geen leden toegevoegd</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export default LedenBeheer; 